import { NO_POS } from './ast.ts';
import type { EvalContext, SourceFetcherCtx } from './EvalContext.ts';
import { createRootEnv } from './env.ts';
import { MaterializationError } from './errors.ts';
import { defaultOpTable, type EvalState, evalOpCall } from './evaluator.ts';
import type { OpCall, Spans } from './normalize.ts';
import type { NameTable } from './op-meta.ts';
import type { LogicalPlan, SourceDescriptor } from './plan.ts';
import { EMPTY_IDENTITY_TABLE } from './schema/row-identity.ts';
import { describeSchema, schemaRefines } from './schema/types.ts';
import {
  type Dataset,
  expectMaterialized,
  isLazy,
  isMaterialized,
  type LazyDataset,
  type MaterializedDataset,
  promoteSchema,
} from './value.ts';

/**
 * Materialiser for `LazyDataset`. Walks a `LogicalPlan` to its source,
 * fetches it via the source's owning provider (resolved by `providerId`
 * through the registry on `EvalContext.getProviderById`), then re-runs
 * each `PlanNode.op` over the materialised source by recursively
 * re-entering `evalOpCall` with the threaded materialised dataset
 * re-prepended to `args.params`. Returns the final `MaterializedDataset`
 * after a schema-refinement post-condition.
 *
 * In scope (conservative shipped behaviour): provider fetch dispatch by
 * `providerId` with a missing-provider diagnostic
 * (`MaterializationError('missing-provider', …)`); op-chain replay via
 * `evalOpCall` (standard arquero path; per-plan-node error positions
 * degrade to `NO_POS` since validation already ran at type-check time);
 * two memoisation layers via `session.fetch` keyed on
 * `['materialize-source', 'provider', providerId, src]` and on
 * `['materialize-plan', source, ops-without-schemas]` so plans sharing
 * a source coalesce and re-materialising the same plan is free;
 * cancellation through `EvalContext.signal` into `SourceFetcherCtx`;
 * outer-only materialisation (`LazyDataset` cells inside the result
 * remain lazy — inner cells force at unnest, scalar coercion, per-cell
 * lift, or explicit `(materialize ds)`); a schema-refinement post-
 * condition that throws `MaterializationError('schema-mismatch', …)`
 * when the op-chain output disagrees with `ds.schema`.
 *
 * Not handled here: server-side push-down beyond what each provider
 * absorbs in its own `plan` / `adaptPlan`. The CDF view and timeseries
 * providers close over a live `CogniteClient` at registration.
 */
export const materialize = async (
  ds: LazyDataset,
  ctx: EvalContext,
  ops: NameTable = defaultOpTable,
): Promise<MaterializedDataset> => {
  return ctx.session.fetch(planCacheKey(ds.plan), async () => {
    const source = await fetchSource(ds.plan, ctx);
    const state: EvalState = {
      spans: emptySpans,
      ops,
      identities: ctx.identities ?? EMPTY_IDENTITY_TABLE,
      evalCtx: ctx,
    };
    const env = createRootEnv(new Set(ops.keys()));
    const final = await ds.plan.ops.reduce<Promise<Dataset>>(async (acc, node, i) => {
      const prev = await acc;
      // Re-prepend the threaded materialised dataset to `args.params`
      // (the plan node stores args with `params[0]` stripped) and re-
      // bind the per-cell lift axis recorded at lift time so a deferred
      // per-cell op re-enters its per-cell form. Without the axis bind,
      // dispatch re-derives the form from `prev` (the inner TS-ref
      // seed, which has none of the outer nested-dataset columns) and
      // misdispatches to the row-level branch.
      const replayCall: OpCall = {
        ...node.args,
        params: [prev, ...(node.args.params ?? [])],
      };
      const replayState: EvalState = { ...state, liftColumn: node.liftColumn };
      const result = await evalOpCall(replayCall, env, planNodePath(i, node.op), replayState);
      return result.value as Dataset;
    }, Promise.resolve<Dataset>(source));
    const materialised = expectMaterialized(final, '(materialize <plan>)', NO_POS);
    // Directional, not exact: `ds.schema` is a conservative static
    // PREDICTION; value-based refinement (`fromTable`) legitimately
    // makes the runtime schema MORE specific than predicted (an
    // `unknown`-predicted column resolving to a concrete type). That
    // refines the prediction, it doesn't violate it. `schemaRefines`
    // still catches a genuine propagator bug (declared `number`,
    // resolved `string`). See `schemaRefines` for the contract.
    if (!schemaRefines(materialised.schema, ds.schema)) {
      throw new MaterializationError(
        'schema-mismatch',
        `materialised schema does not refine the declared LazyDataset schema: actual={${describeSchema(materialised.schema)}}, declared={${describeSchema(ds.schema)}}`,
        NO_POS,
      );
    }
    // Promote the declared schema onto the replay result. The replay re-derives
    // the result schema from the raw source, typing a discriminator-less
    // TimeSeries ref as `timeseries<unknown>` with no unit — the declaration
    // fills those holes. Units flow the OTHER way: the replay schema carries
    // cell-derived units (`refineSchema` per replayed op) that the unit-less
    // static declaration must not strip — `columnTypeJoin` merges them
    // evidence-first inside the shared `promoteSchema` boundary primitive. The
    // guard above runs first, so a genuine propagator bug is still rejected
    // before the join can lift the conservative prediction back to the declaration.
    return promoteSchema(materialised, ds.schema) as MaterializedDataset;
  });
};

/**
 * Materialise a `Dataset` regardless of subtype: already-materialised
 * inputs short-circuit to a resolved promise; `LazyDataset`s route
 * through {@link materialize}. The defensive `throw` is unreachable
 * in practice — every concrete `Dataset` is either materialised or
 * lazy — but TypeScript can't infer that closed-union shape from the
 * abstract base, and a runtime error is preferable to a silent
 * `undefined` if a third subtype is ever added.
 *
 * Used by React-side hooks (`useMaterialize`, `useMaterializedExpr`)
 * so the narrow-or-throw dance lives in one place.
 */
export const materializeIfLazy = (
  ds: Dataset,
  ctx: EvalContext,
  ops: NameTable = defaultOpTable,
): Promise<MaterializedDataset> => {
  if (isMaterialized(ds)) {
    return Promise.resolve(ds);
  }
  if (isLazy(ds)) {
    return materialize(ds, ctx, ops);
  }
  throw new TypeError(`materializeIfLazy: unrecognised Dataset subtype (state=${(ds as Dataset).state})`);
};

/**
 * Resolve a {@link LogicalPlan} to a `MaterializedDataset`.
 *
 * Every source is a `provider` descriptor: it resolves its owning
 * provider through the registry on `EvalContext` (`getProviderById`,
 * keyed on the descriptor's `providerId`), optionally refines the source
 * via the provider's `adaptPlan` (HINT-style lazy absorption of a
 * downstream op that stays in the plan), and runs the provider's `fetch`.
 * A provider id with no registered provider — or no registry wired at all
 * — surfaces as `MaterializationError('missing-provider')`. A provider
 * that claims a dataset but supplies no `fetch` is a wiring bug (a facade
 * should rewrite, not reach materialisation), reported the same way.
 *
 * The source-level cache is keyed via `sourceCacheKey` so distinct
 * pushdowns (and distinct providers) cache distinctly, and the provider's
 * `fetch` is handed a narrow `SourceFetcherCtx` (session + signal) — it
 * may not recurse back into expression evaluation. The substrate stays
 * op-agnostic: only a provider's `adaptPlan` knows which ops it pushes
 * down.
 */
const fetchSource = (plan: LogicalPlan, ctx: EvalContext): Promise<MaterializedDataset> => {
  const src = plan.source;
  const sfCtx: SourceFetcherCtx = { session: ctx.session, signal: ctx.signal };

  const provider = ctx.getProviderById?.(src.providerId);
  if (provider?.fetch === undefined) {
    const why =
      provider === undefined
        ? `no provider with id '${src.providerId}' is registered on EvalContext.getProviderById`
        : `provider '${src.providerId}' resolves but supplies no fetch (a facade must rewrite, not materialise)`;
    throw new MaterializationError('missing-provider', why, NO_POS);
  }
  const fetch = provider.fetch;
  // Lazy HINT-style absorption: a provider may refine its source from a
  // downstream op that stays in `plan.ops` (e.g. the timeseries provider
  // absorbing a per-cell `time-bucket`'s window / aggregate). Key the
  // source cache on the ADAPTED source so distinct push-downs cache
  // distinctly; the op is not removed, so the plan-level cache key is
  // unchanged (the replay still runs — the hint only narrows the fetch).
  const adapted = provider.adaptPlan?.(plan, src) ?? src;
  return ctx.session.fetch(sourceCacheKey(adapted), () => fetch(adapted, sfCtx));
};

/**
 * Stable cache key for an entire `LogicalPlan`. Excludes the cached
 * `schemaAfter` field on each `PlanNode` because `Schema` is a `Map`
 * (and `Map`s don't JSON-serialise faithfully — the `Session.fetch`
 * default hash uses `JSON.stringify`); only `(op, args)` is keying-
 * relevant since `schemaAfter` is derivable from those two via the
 * op's `propagateSchema`. `liftColumn` IS keying-relevant: two
 * otherwise-identical plans lifted on different axes replay
 * differently, so it must not collide in the memo.
 */
const planCacheKey = (plan: LogicalPlan): readonly unknown[] => [
  'materialize-plan',
  plan.source,
  plan.ops.map(({ op, args, liftColumn }) => ({ op, args, liftColumn })),
];

/** Per-source cache key. Discriminator + descriptor — the descriptor's
 * own structure (the provider's `native` blob, JSON-serialisable) is
 * hashable by the standard `Session.fetch` hash.
 *
 * Every source is a `provider` descriptor sharing the one `'provider'`
 * discriminator, so `providerId` is folded in as a distinct key segment:
 * it keeps two different providers' sources from colliding, and (together
 * with the descriptor's `native` blob) is the source-level cache identity
 * — identical queries against one provider coalesce, distinct pushdowns
 * cache distinctly. */
const sourceCacheKey = (src: SourceDescriptor): readonly unknown[] => [
  'materialize-source',
  src.kind,
  src.providerId,
  src,
];

/**
 * Synthesised path for plan-node error attribution. Real source
 * positions were dropped when the plan was built (the original op
 * call's `path` was call-site-relative, not plan-relative), so we
 * fabricate a path that at least identifies the offending op and its
 * index within the plan.
 */
const planNodePath = (i: number, op: string): string => `materialize/${i}/${op}`;

/** Empty spans sidecar — materialiser-time errors have no source
 * positions (errors during plan replay surface with `NO_POS`). */
const emptySpans: Spans = new Map();
